/**
 * Account Settings Page
 *
 * Displays account information and FAL verification status for broker/company users.
 * Broker and company users see their FAL license status with a re-submit form if rejected.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import type { FalVerificationStatus } from '../../hooks/useAuth';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import { useToast } from '../../components/common/ToastProvider';
import { resubmitFalLicense } from '../../services/registrationService';

export default function AccountPage() {
  const { t, i18n } = useTranslation(['registration', 'common', 'settings']);
  const { user, session, signOut } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const isRTL = i18n.dir() === 'rtl';

  // FAL re-submit form state
  const [falNumber, setFalNumber] = useState(user?.falLicenseNumber ?? '');
  const [crNumber, setCrNumber] = useState(user?.crNumber ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResubmitForm, setShowResubmitForm] = useState(false);

  // Local FAL status override after successful re-submit
  const [localFalStatus, setLocalFalStatus] = useState<FalVerificationStatus | undefined>(
    user?.falVerificationStatus
  );

  const isBrokerOrCompany =
    user?.accountType === 'broker' || user?.accountType === 'company' ||
    user?.role === 'BROKER' || user?.role === 'COMPANY_ADMIN';

  const falStatus: FalVerificationStatus =
    localFalStatus ?? user?.falVerificationStatus ?? 'PENDING';

  const rejectionReason = user?.falRejectionReason as string | undefined;

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleResubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session?.token) return;

    setIsSubmitting(true);
    try {
      await resubmitFalLicense(
        falNumber,
        session.token,
        user?.accountType === 'company' ? crNumber : undefined
      );
      setLocalFalStatus('PENDING');
      setShowResubmitForm(false);
      showToast(t('registration:falStatus.resubmitSuccess'), 'success');
    } catch (err) {
      console.error('FAL re-submit error:', err);
      showToast(t('registration:falStatus.submitError'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <i className="ri-user-line text-6xl text-gray-400 mb-4 block"></i>
            <p className="text-gray-600 mb-4">
              {t('common:auth.signInRequired', { defaultValue: 'Please sign in to view your account.' })}
            </p>
            <Link to="/signin">
              <Button>
                {t('common:auth.signIn', { defaultValue: 'Sign In' })}
              </Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 rounded-xl bg-[rgba(0,77,64,0.15)]">
              <i className="ri-user-settings-line text-xl text-[var(--pv-color-primary)]"></i>
            </div>
            <h1 className="text-2xl font-bold text-[var(--pv-color-primary)]">
              {t('settings:account.title', { defaultValue: 'Account' })}
            </h1>
          </div>
          <p className="text-gray-500 text-sm">
            {t('settings:account.description', { defaultValue: 'Manage your account information and settings.' })}
          </p>
        </div>

        <div className="space-y-6">
          {/* Account Info Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-base font-semibold text-[var(--pv-color-primary)] mb-4">
              {t('settings:account.editProfile', { defaultValue: 'Profile Information' })}
            </h2>

            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-full bg-[rgba(0,77,64,0.25)] flex items-center justify-center flex-shrink-0">
                <i className="ri-user-line text-2xl text-[var(--pv-color-primary)]"></i>
              </div>
              <div>
                <p className="font-semibold text-gray-900">
                  {user.firstName || user.lastName
                    ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
                    : t('settings:account.noName', { defaultValue: 'No name set' })}
                </p>
                <p className="text-sm text-gray-500">{user.email}</p>
                {user.accountType && (
                  <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700">
                    {user.accountType === 'broker'
                      ? t('registration:userType.broker.title')
                      : user.accountType === 'company'
                      ? t('registration:userType.company.title')
                      : t('registration:userType.individual.title')}
                  </span>
                )}
                {/* FAL-license distinction for individuals who have listed under
                    Safaqat24's license vs a plain individual with no properties. */}
                {user.accountType === 'individual' && user.hasFalListing && (
                  <span className="inline-flex items-center gap-1 mt-1 ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                    <i className="ri-verified-badge-fill"></i>
                    {t('common:badges.falIndividual', 'Individual (Safaqat24 license)')}
                  </span>
                )}
              </div>
            </div>

            <div className="flex gap-3 flex-wrap">
              <Link to="/profile">
                <Button variant="outline" size="sm">
                  <i className={`ri-edit-line ${isRTL ? 'ml-2' : 'mr-2'}`}></i>
                  {t('settings:account.editProfile', { defaultValue: 'Edit Profile' })}
                </Button>
              </Link>
              <Link to="/billing">
                <Button variant="outline" size="sm">
                  <i className={`ri-receipt-line ${isRTL ? 'ml-2' : 'mr-2'}`}></i>
                  {t('billing:pageTitle', { defaultValue: 'Payments & Invoices' })}
                </Button>
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSignOut}
                className="text-gray-600 border-gray-300 hover:bg-gray-50"
              >
                <i className={`ri-logout-circle-line ${isRTL ? 'ml-2' : 'mr-2'}`}></i>
                {t('settings:account.signOut', { defaultValue: 'Sign Out' })}
              </Button>
            </div>
          </div>

          {/* Nafath Identity Verification — broker/agent only */}
          {isBrokerOrCompany && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-600 flex items-center justify-center flex-shrink-0">
                  <i className="ri-fingerprint-line text-2xl text-white" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base font-semibold text-[var(--pv-color-primary)] mb-1">
                    {isRTL ? 'التحقق من الهوية (نفاذ)' : 'Identity verification (Nafath)'}
                  </h2>
                  {user.nafathVerificationStatus === 'VERIFIED' ? (
                    <p className="text-sm text-green-700 mb-3">
                      {isRTL ? '✓ تم التحقق من هويتك عبر نفاذ.' : '✓ Your identity is verified via Nafath.'}
                      {user.nationalIdLastFour && (isRTL
                        ? ` (الهوية: •••••• ${user.nationalIdLastFour})`
                        : ` (ID: •••••• ${user.nationalIdLastFour})`)}
                    </p>
                  ) : user.nafathVerificationStatus === 'PENDING' ? (
                    <p className="text-sm text-amber-700 mb-3">
                      {isRTL
                        ? 'طلب التحقق جاري. افتح تطبيق نفاذ لإكماله.'
                        : 'Verification in progress - open the Nafath app on your phone to complete it.'}
                    </p>
                  ) : user.nafathVerificationStatus === 'FAILED' ? (
                    <p className="text-sm text-red-700 mb-3">
                      {isRTL
                        ? 'فشل التحقق. يرجى المحاولة مرة أخرى.'
                        : 'Verification failed. Please try again.'}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-600 mb-3">
                      {isRTL
                        ? 'تحقق من هويتك عبر تطبيق نفاذ لتفعيل ترخيص الفال تلقائياً ونشر العقارات.'
                        : 'Verify your identity via the Nafath app to auto-activate your FAL license and start listing properties.'}
                    </p>
                  )}
                  <Link to="/account/verify-identity">
                    <Button
                      variant={user.nafathVerificationStatus === 'VERIFIED' ? 'outline' : 'primary'}
                      size="sm"
                    >
                      <i className={`ri-fingerprint-line ${isRTL ? 'ml-2' : 'mr-2'}`} aria-hidden="true" />
                      {user.nafathVerificationStatus === 'VERIFIED'
                        ? (isRTL ? 'عرض التفاصيل' : 'View details')
                        : (isRTL ? 'التحقق الآن' : 'Verify now')}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Grow your account — add professional roles / register a company */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-base font-semibold text-[var(--pv-color-primary)] mb-1">
              {isRTL ? 'طوّر حسابك' : 'Grow your account'}
            </h2>
            <p className="text-sm text-gray-600 mb-3">
              {isRTL
                ? 'أضف دوراً مهنياً مرخصاً (وسيط، مطوّر، مستشار، مقيّم...) أو سجّل شركة تملكها.'
                : 'Add a licensed professional role (broker, developer, consultant, appraiser…) or register a company you own.'}
            </p>
            <Link to="/account/professional">
              <Button variant="primary" size="sm">
                <i className={`ri-briefcase-line ${isRTL ? 'ml-2' : 'mr-2'}`} aria-hidden="true" />
                {isRTL ? 'إدارة الأدوار المهنية' : 'Manage professional roles'}
              </Button>
            </Link>
          </div>

          {/* FAL License Verification — only for broker and company users */}
          {isBrokerOrCompany && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-base font-semibold text-[var(--pv-color-primary)] mb-4">
                {t('registration:falStatus.title')}
              </h2>

              {/* Status indicator */}
              {falStatus === 'APPROVED' && (
                <div className="flex items-start gap-3 mb-4 p-3 rounded-xl bg-green-50 border border-green-100">
                  <div className="flex-shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-fill text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      {t('registration:falStatus.verified')}
                    </p>
                    {user.falLicenseNumber && (
                      <p className="text-xs text-green-600 mt-0.5">
                        {t('registration:falStatus.licenseNumber')}: {user.falLicenseNumber}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {falStatus === 'PENDING' && (
                <div className="flex items-start gap-3 mb-4 p-3 rounded-xl bg-amber-50 border border-amber-100">
                  <div className="flex-shrink-0 mt-0.5">
                    <i className="ri-time-line text-amber-600 text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-amber-800">
                      {t('registration:falStatus.pending')}
                    </p>
                    <p className="text-xs text-amber-600 mt-0.5">
                      {t('registration:falStatus.pendingNote')}
                    </p>
                  </div>
                </div>
              )}

              {falStatus === 'REJECTED' && (
                <div className="mb-4">
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-red-50 border border-red-100 mb-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <i className="ri-close-circle-fill text-red-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-red-800">
                        {t('registration:falStatus.rejected')}
                      </p>
                      {rejectionReason && (
                        <div className="mt-2 p-2 rounded-lg border border-red-200 bg-red-50/60">
                          <p className="text-xs text-red-600 font-medium mb-0.5">
                            {t('registration:falStatus.rejectionReason')}:
                          </p>
                          <p className="text-xs text-red-700">{rejectionReason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Re-submit form: shown for REJECTED status, or toggled for APPROVED/PENDING */}
              {(falStatus === 'REJECTED' || showResubmitForm) ? (
                <form onSubmit={handleResubmit} className="space-y-4 pt-2 border-t border-gray-100">
                  <p className="text-sm font-medium text-gray-700">
                    {falStatus === 'REJECTED'
                      ? t('registration:falStatus.resubmit')
                      : t('registration:falStatus.updateLicense')}
                  </p>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {t('registration:falStatus.licenseNumber')}
                    </label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      maxLength={12}
                      value={falNumber}
                      onChange={(e) => setFalNumber(e.target.value.replace(/\D/g, ''))}
                      placeholder={t('registration:businessInfo.falLicensePlaceholder')}
                      disabled={isSubmitting}
                      required
                    />
                  </div>

                  {user.accountType === 'company' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t('registration:falStatus.crNumber')}
                      </label>
                      <Input
                        type="text"
                        value={crNumber}
                        onChange={(e) => setCrNumber(e.target.value)}
                        placeholder={t('registration:businessInfo.crNumberPlaceholder')}
                        disabled={isSubmitting}
                      />
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button type="submit" disabled={isSubmitting || !/^[0-9]{7,12}$/.test(falNumber.trim())}>
                      {isSubmitting
                        ? t('registration:falStatus.submitting')
                        : t('registration:falStatus.resubmit')}
                    </Button>
                    {showResubmitForm && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowResubmitForm(false)}
                        disabled={isSubmitting}
                      >
                        {t('registration:buttons.cancel')}
                      </Button>
                    )}
                  </div>
                </form>
              ) : (
                falStatus === 'APPROVED' && (
                  <button
                    type="button"
                    onClick={() => setShowResubmitForm(true)}
                    className="text-sm text-[var(--pv-color-primary)] hover:underline focus:outline-none"
                  >
                    {t('registration:falStatus.updateLicense')}
                  </button>
                )
              )}
            </div>
          )}

          {/* Quick Links */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-base font-semibold text-[var(--pv-color-primary)] mb-4">
              {t('settings:sections.account', { defaultValue: 'Account Settings' })}
            </h2>
            <div className="space-y-2">
              <Link
                to="/settings"
                className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-gray-100 group-hover:bg-purple-50 transition-colors">
                    <i className="ri-settings-4-line text-gray-500 group-hover:text-[var(--pv-color-primary)] transition-colors"></i>
                  </div>
                  <span className="text-sm text-gray-700">
                    {t('settings:title', { defaultValue: 'Settings' })}
                  </span>
                </div>
                <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-s-line text-gray-400`}></i>
              </Link>

              <Link
                to="/profile"
                className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-gray-100 group-hover:bg-purple-50 transition-colors">
                    <i className="ri-user-line text-gray-500 group-hover:text-[var(--pv-color-primary)] transition-colors"></i>
                  </div>
                  <span className="text-sm text-gray-700">
                    {t('settings:account.editProfile', { defaultValue: 'Edit Profile' })}
                  </span>
                </div>
                <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-s-line text-gray-400`}></i>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
